import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'gold';

/**
 * The Figma "Colors" collection. Tokens that have a natural MUI home are mapped
 * onto the standard slots below (primary.*, text.*, background.*, divider); the
 * rest are exposed as these palette groups so every colour in the app resolves
 * through `theme.palette`.
 */
export interface BrandPalette {
  navy: string;
  /** Lighter navy used as the Login radial-gradient's inner stop. Not in the Figma
   *  "Colors" collection — pending a token from the design team. */
  navyLight: string;
  lucht: string;
  luchtHover: string;
  ocean: string;
  icon: string;
  cyanHead: string;
}
export interface SurfacePalette {
  canvas: string;
  cyan: string;
  cyanHover: string;
  input: string;
}
export interface BorderPalette {
  subtle: string;
  strong: string;
  soft: string;
  cyan: string;
  grey: string;
}
/** Categorical series ramps for the dashboard charts. */
export interface ChartPalette {
  /** Bars render on the navy card — a light cyan ramp. */
  bars: string[];
  /** Lines render on the light-cyan card — a deep teal ramp. */
  lines: string[];
}
export interface StatusPalette {
  successBg: string;
  successFg: string;
  successBorder: string;
  runningBg: string;
  runningFg: string;
  failBg: string;
  failFg: string;
  warnBg: string;
  warnFg: string;
  neutralBg: string;
  neutralFg: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    gold: PaletteColor;
    brand: BrandPalette;
    surface: SurfacePalette;
    border: BorderPalette;
    status: StatusPalette;
    chart: ChartPalette;
  }
  interface PaletteOptions {
    gold?: PaletteColor;
    brand?: BrandPalette;
    surface?: SurfacePalette;
    border?: BorderPalette;
    status?: StatusPalette;
    chart?: ChartPalette;
  }
  interface TypeBackground {
    neutral: string;
  }
  interface TypeText {
    tertiary: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
    gradient: {
      main: string;
      bold: string;
    };
  }

  interface PaletteColor {
    lighter: string;
    darker: string;
    gradient?: {
      main: string;
      bold: string;
    };
  }

  interface Color {
    0: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    '500Transparent': {
      8: string;
      12: string;
      16: string;
      20: string;
      24: string;
      32: string;
      48: string;
      80: string;
    };
  }
  interface CommonColors {
    blackTransparent: ReturnType<typeof generateTransparencyShades>;
    whiteTransparent: ReturnType<typeof generateTransparencyShades>;
  }
}

export const generateTransparencyShades = (color: string) => ({
  8: alpha(color, 0.08),
  12: alpha(color, 0.12),
  16: alpha(color, 0.16),
  20: alpha(color, 0.2),
  24: alpha(color, 0.24),
  32: alpha(color, 0.32),
  48: alpha(color, 0.48),
  80: alpha(color, 0.8)
});

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  '500Transparent': generateTransparencyShades('#919EAB')
};

// Bestek AI brand — Figma "Colors" collection.
const BRAND: BrandPalette = {
  navy: '#014A4E',
  navyLight: '#076166',
  lucht: '#01E1ED',
  luchtHover: '#0ACCD6',
  ocean: '#30C7CF',
  icon: '#04A3AB',
  cyanHead: '#4AEAF2'
};

const SURFACE: SurfacePalette = {
  canvas: '#F5F6F8',
  cyan: '#E5FCFD',
  cyanHover: '#F8FCFE',
  input: '#F8FAFB'
};

const BORDER: BorderPalette = {
  subtle: '#ECEEF1',
  strong: '#DEE2E7',
  soft: '#F2F4F6',
  cyan: '#C2F8FB',
  grey: '#C7CDD4'
};

/**
 * Text. `secondary` is nudged one step darker than the Figma value (#6E7884)
 * so body copy clears WCAG 1.4.3 (4.5:1) on both white and surface.input — it
 * was 4.48:1 / 4.28:1. `disabled` is for genuinely disabled UI only; it is far
 * below 4.5:1 and must never carry body text (UI/UX audit 2.1.3).
 */
const        TEXT = {
  primary: BRAND.navy,
  secondary: '#6A7480',
  disabled: '#9BA3AE',
  tertiary: '#575656'
};

/** Foregrounds darkened to clear 4.5:1 on their own backgrounds (audit 2.4.6, 2.9.1). */
const STATUS: StatusPalette = {
  successBg: '#E4F4EC',
  successFg: '#1B7C51',
  successBorder: '#B7E3CC',
  runningBg: SURFACE.cyan,
  runningFg: '#037D83',
  failBg: '#FDECEA',
  failFg: '#C23E3E',
  warnBg: '#FFF1DB',
  warnFg: '#976419',
  neutralBg: BORDER.soft,
  neutralFg: TEXT.secondary
};

/**
 * Categorical chart ramps. The previous ramps were near-identical cyan shades,
 * so series could not be told apart (audit 2.2.1, WCAG 1.4.1). These are
 * separated by hue AND lightness so they survive greyscale and colour-blindness.
 * Bars render on the navy card (light values); lines on the light-cyan card
 * (dark values). Pending design sign-off on the exact hues.
 */
const CHART: ChartPalette = {
  bars: [BRAND.lucht, '#FFD666', '#77ED8B', '#FFAC82', '#B79CFF', '#8FF2F7', '#FF8A9B', '#C4CDD5'],
  lines: [BRAND.navy, '#04A3AB', '#B7791F', '#1F8A5B', '#8E44AD', '#C23E3E']
};

const PRIMARY = {
  lighter: SURFACE.cyan,
  light: '#5EE2EC', 
  main: BRAND.lucht,
  dark: BRAND.navy,
  darker: BRAND.navy,
  // Ink on the cyan primary. White was 1.62:1 — it failed WCAG 1.4.3 on every
  // CTA in the app and read as a disabled control (audit 2.1.1, 2.1.2, 3).
  // Navy is 6.21:1.
  contrastText: '#014A4F',
  gradient: {
    main: `linear-gradient(135deg, #5EE2EC 0%, #009CA7 100%)`,
    bold: 'linear-gradient(135deg, #009CA7 0%, #006971 100%)' 
  }
};

const SECONDARY = {
  lighter: '#F9CAC7',
  light: '#EC685E',
  main: '#D14343',
  dark: '#D14343',
  darker: '#4F0601',
  contrastText: '#FFFFFF',
  gradient: {
    main: 'linear-gradient(135deg, #858585 0%, #303030 100%)',
    bold: 'linear-gradient(135deg, #303030 0%, #0A0A0A 100%)'
  }
};

const INFO = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#04A3AB',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#FFFFFF',
  gradient: {
    main: 'linear-gradient(135deg, #61F3F3 0%, #00B8D9 100%)',
    bold: 'linear-gradient(135deg, #00B8D9 0%, #006C9C 100%)'
  }
};

const SUCCESS = {
  lighter: '#D3FCD2',
  light: '#77ED8B',
  main: '#1F8A5B',
  dark: '#118D57',
  darker: '#065E49',
  contrastText: '#FFFFFF',
  gradient: {
    main: 'linear-gradient(135deg, #77ED8B 0%, #22C55E 100%)',
    bold: 'linear-gradient(135deg, #22C55E 0%, #118D57 100%)'
  }
};

const WARNING = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#B7791F',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: '#000000',
  gradient: {
    main: 'linear-gradient(135deg, #FFD666 0%, #FFAB00 100%)',
    bold: 'linear-gradient(135deg, #FFAB00 0%, #B76E00 100%)'
  }
};

const ERROR = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#D14343',
  dark: '#D14343',
  darker: '#7A0916',
  contrastText: '#FFFFFF',
  gradient: {
    main: 'linear-gradient(135deg, #FFAC82 0%, #FF5630 100%)',
    bold: 'linear-gradient(135deg, #FF5630 0%, #B71D18 100%)'
  }
};

const GOLD = {
  light: '#FFEA60'
};

const COMMON = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
    blackTransparent: generateTransparencyShades('#000000'),
    whiteTransparent: generateTransparencyShades('#FFFFFF')
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  gold: GOLD,
  divider: GREY['500Transparent'][20],
  action: {
    active: GREY[500],
    hover: GREY['500Transparent'][8],
    selected: GREY['500Transparent'][16],
    disabled: GREY['500Transparent'][80],
    disabledBackground: GREY['500Transparent'][24],
    focus: GREY['500Transparent'][24],
    hoverOpacity: 0.08,
    selectedOpacity: 0.08,
    disabledOpacity: 0.48,
    focusedOpacity: 0.12,
    activatedOpacity: 0.12
  }
};

export function palette(mode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    divider: BORDER.subtle,
    text: { ...TEXT },
    background: {
      paper: COMMON.common.white,
      default: COMMON.common.white,
      neutral: SURFACE.canvas
    },
    brand: BRAND,
    surface: SURFACE,
    border: BORDER,
    status: STATUS,
    chart: CHART,
    action: {
      ...COMMON.action,
      active: GREY[500]
    }
  };

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: COMMON.common.white,
      secondary: GREY[500],
      disabled: GREY[600],
      tertiary: GREY[400]
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: GREY['500Transparent'][12]
    },
    brand: BRAND,
    surface: SURFACE,
    border: BORDER,
    status: STATUS,
    chart: CHART,
    action: {
      ...COMMON.action,
      active: GREY[500]
    }
  };

  return mode === 'light' ? light : dark;
}
